import {useState} from 'react';
import {formatCurrency} from '@common/lib/utils';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@common-web/components/ui/sheet';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {DollarSign, Users, FileText, Hash, ChevronDown, ChevronRight} from 'lucide-react';

interface SubscriberSheetProps {
  invoiceKey: number;
  subscriberId: string;
  onClose: () => void;
}

type BreakdownItem =
  RouterOutput['internal']['invoice']['getSubscriberPremiumBreakdown']['breakdown'][number];

export function SubscriberSheet({invoiceKey, subscriberId, onClose}: SubscriberSheetProps) {
  const [expandedRates, setExpandedRates] = useState<Set<number>>(new Set());

  const {data, isLoading} = trpc.internal.invoice.getSubscriberPremiumBreakdown.useQuery({
    invoiceKey,
    subscriberId,
  });

  const toggleRate = (rateKey: number) => {
    setExpandedRates((prev) => {
      const next = new Set(prev);
      if (next.has(rateKey)) {
        next.delete(rateKey);
      } else {
        next.add(rateKey);
      }
      return next;
    });
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className='sm:max-w-lg overflow-y-auto'>
        <SheetHeader className='pb-4 border-b'>
          <SheetTitle className='text-xl font-bold text-brand-navy'>
            {isLoading ? <Skeleton className='h-7 w-48' /> : data?.name}
          </SheetTitle>
          <SheetDescription className='flex flex-col gap-1 text-sm'>
            {isLoading ? (
              <>
                <Skeleton className='h-4 w-36' />
                <Skeleton className='h-4 w-32' />
              </>
            ) : (
              <>
                <span>
                  <span className='font-medium text-brand-gray'>Subscriber ID:</span>{' '}
                  <span className='font-mono'>{data?.subscriberId}</span>
                </span>
                {data?.alternateId && (
                  <span>
                    <span className='font-medium text-brand-gray'>Alternate ID:</span>{' '}
                    <span className='font-mono'>{data.alternateId}</span>
                  </span>
                )}
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className='space-y-4 mt-6'>
            <div className='grid grid-cols-2 gap-3'>
              <Skeleton className='h-24 w-full' />
              <Skeleton className='h-24 w-full' />
            </div>
            <Skeleton className='h-64 w-full' />
          </div>
        ) : data ? (
          <div className='space-y-6 mt-6'>
            {/* Summary Cards */}
            <div className='grid grid-cols-2 gap-3'>
              <Card className='border-gray-200'>
                <CardContent className='pt-4 pb-3'>
                  <div className='flex items-center gap-2 text-xs text-brand-gray mb-1'>
                    <DollarSign className='h-3.5 w-3.5 text-emerald' />
                    Total Premium
                  </div>
                  <div className='text-xl font-bold text-brand-navy'>
                    {formatCurrency(data.totalPremium)}
                  </div>
                </CardContent>
              </Card>

              <Card className='border-gray-200'>
                <CardContent className='pt-4 pb-3'>
                  <div className='flex items-center gap-2 text-xs text-brand-gray mb-1'>
                    <Users className='h-3.5 w-3.5 text-brand-blue' />
                    Members
                  </div>
                  <div className='text-xl font-bold text-brand-navy'>
                    {data.memberCount}
                    {data.dependentCount > 0 && (
                      <span className='text-sm font-normal text-brand-gray ml-1'>
                        (+{data.dependentCount} dep)
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Premium Breakdown Table */}
            <Card className='border-gray-200'>
              <CardHeader className='bg-gray-50 border-b py-3'>
                <CardTitle className='text-sm font-semibold text-brand-navy flex items-center gap-2'>
                  <FileText className='h-4 w-4 text-brand-blue' />
                  Premium Breakdown
                  <Badge className='bg-brand-blue/10 text-brand-blue border-0 ml-auto text-xs'>
                    {data.breakdown.length} rates
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className='p-0'>
                {data.breakdown.length === 0 ? (
                  <div className='p-6 text-center text-brand-gray'>No premium breakdown found</div>
                ) : (
                  <>
                    <div className='divide-y'>
                      {data.breakdown.map((item: BreakdownItem) => {
                        const isExpanded = expandedRates.has(item.premRateKey);
                        const hasPayees = item.payees.length > 0;

                        return (
                          <div key={item.premRateKey}>
                            <div
                              className={`p-3 hover:bg-gray-50 ${hasPayees ? 'cursor-pointer' : ''}`}
                              onClick={() => hasPayees && toggleRate(item.premRateKey)}
                            >
                              <div className='flex justify-between items-start'>
                                <div className='flex items-start gap-2'>
                                  {hasPayees && (
                                    <span className='text-brand-gray mt-0.5'>
                                      {isExpanded ? (
                                        <ChevronDown className='h-4 w-4' />
                                      ) : (
                                        <ChevronRight className='h-4 w-4' />
                                      )}
                                    </span>
                                  )}
                                  {!hasPayees && <span className='w-4' />}
                                  <div>
                                    <div className='font-medium text-brand-navy text-sm'>
                                      {item.description}
                                    </div>
                                    <div className='text-xs text-brand-gray font-mono flex items-center gap-1'>
                                      <Hash className='h-3 w-3' />
                                      {item.premRateKey}
                                      {hasPayees && (
                                        <span className='ml-2 text-purple-500'>
                                          ({item.payees.length} payee
                                          {item.payees.length !== 1 ? 's' : ''})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className='text-right'>
                                  <div className='font-mono font-medium text-brand-navy'>
                                    {formatCurrency(item.premium)}
                                  </div>
                                  <div className='text-xs text-brand-gray font-mono'>
                                    Vol: {item.volume.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Nested Payees */}
                            {isExpanded && hasPayees && (
                              <div className='bg-purple-50/50 border-t border-purple-100'>
                                <div className='px-3 py-2 text-xs font-semibold text-purple-600 uppercase tracking-wider flex items-center gap-1'>
                                  <Users className='h-3 w-3' />
                                  Payee Breakdown
                                </div>
                                <div className='divide-y divide-purple-100'>
                                  {item.payees.map((payee, pidx) => (
                                    <div
                                      key={pidx}
                                      className='px-3 py-2 pl-9 flex justify-between items-center text-sm'
                                    >
                                      <div>
                                        <span className='text-brand-navy'>{payee.payeeName}</span>
                                        <span className='text-xs text-brand-gray ml-2 font-mono'>
                                          ({payee.payeeId})
                                        </span>
                                        {payee.distType && (
                                          <Badge className='ml-2 text-xs py-0 px-1 bg-purple-100 text-purple-600 border-0'>
                                            {payee.distType}
                                          </Badge>
                                        )}
                                      </div>
                                      <span className='font-mono text-brand-navy'>
                                        {formatCurrency(payee.amount)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Total Footer */}
                    <div className='p-3 bg-gray-50 border-t'>
                      <div className='flex justify-between items-center'>
                        <span className='font-medium text-brand-gray'>Total Premium</span>
                        <span className='font-mono font-bold text-brand-navy text-lg'>
                          {formatCurrency(data.totalPremium)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
